import type { Security } from '@ovComponents/3-pattern/securitiesSelectionTable/types';
import {
  getHouseholdModelPortfolioSecurities,
  groupHouseholdModelPortfolioSecuritiesByAssetClass,
  isHouseholdModelPortfolioAssetClassStrategy,
} from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.allocation.utils';
import type {
  HouseholdModelPortfolioChild,
  HouseholdModelPortfolioModel,
} from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import { round, sumBy } from 'lodash-es';
import { validateGroupAllocations } from '../utils/useAssetClassGrouping';
import { isBranchAllocationInvalid } from '../utils/useSaveModelPortfolio';

/** The id a row carries until a security is picked for it. */
const TEMP_ID_PREFIX = 'temp-';

/**
 * The household's model portfolio as the securities step leaves it.
 *
 * That step is shared with the shelf model flow and works in rows rather than
 * in the holdings a model is made of, so it writes its work alongside them.
 */
export type EditedHouseholdModelPortfolio = HouseholdModelPortfolioModel & {
  securities?: Security[];
};

const toChild = (
  security: Security,
  held?: HouseholdModelPortfolioChild,
): HouseholdModelPortfolioChild => ({
  ...held,
  percentage: security.percentage,
  isCashEquivalent:
    security.isCashEquivalent ?? held?.isCashEquivalent ?? false,
  taxRank: security.taxRank ?? held?.taxRank,
  assetClass: held?.assetClass ?? security.assetClass,
  // A security picked in the step brings the whole product with it, which is
  // what a holding the model never had has to be built out of.
  financialProduct: held?.financialProduct ?? {
    id: security.financialProduct.id,
    name: security.financialProduct.name,
    ticker: security.financialProduct.ticker ?? undefined,
    isCash: security.financialProduct.isCash,
    taxRank: security.financialProduct.taxRank ?? undefined,
    translatedName: security.financialProduct.translatedName,
    primaryAssetClass: security.financialProduct.primaryAssetClass,
    secondaryAssetClass: security.financialProduct.secondaryAssetClass,
    tertiaryAssetClass: security.financialProduct.tertiaryAssetClass,
  },
});

/**
 * The model as the advisor left it, in the shape everything downstream reads.
 *
 * The rows are the newer word on the holdings, so they replace them outright:
 * one dropped in the step is gone from the model, and the order the advisor put
 * them in is kept. What the model already knew about a holding it still has —
 * which asset class it sits in, and its own id — is carried across rather than
 * rebuilt from the row, which only knows what the table shows.
 *
 * A row left without a security is left out. It cannot be proposed, and the
 * weight it was holding is missing from the total either way, which is the
 * thing worth stopping on.
 */
export const withEditedSecurities = (
  model: EditedHouseholdModelPortfolio,
): HouseholdModelPortfolioModel => {
  const { securities, ...portfolio } = model;
  if (!securities) return portfolio;

  const heldByProduct = new Map(
    (portfolio.children ?? [])
      .filter((child) => !!child.financialProduct?.id)
      .map((child) => [child.financialProduct?.id as string, child]),
  );

  return {
    ...portfolio,
    children: securities
      .filter(
        (
          security,
        ): security is Security & { financialProduct: { id: string } } =>
          Boolean(
            security.financialProduct.id &&
              !security.financialProduct.id.startsWith(TEMP_ID_PREFIX),
          ),
      )
      .map((security) =>
        toChild(security, heldByProduct.get(security.financialProduct.id)),
      ),
  };
};

/**
 * What the model holds as things stand.
 *
 * Steps that only read the holdings — to total them against the bands, say —
 * want the rows folded in too, since a step that has been through leaves its
 * work there and not in the holdings themselves. Before that step, and on the
 * branches that never reach it, this is just what the model came with.
 */
export const editedHoldings = (
  model?: EditedHouseholdModelPortfolio,
): HouseholdModelPortfolioChild[] | undefined =>
  model ? withEditedSecurities(model).children : undefined;

/** What stops the holdings from describing a model that can be proposed. */
export type HoldingsProblem =
  | 'EMPTY'
  | 'DUPLICATE'
  | 'WEIGHTS'
  | 'GROUP_HOLDINGS'
  | 'GROUP_WEIGHTS';

/**
 * What, if anything, stops these holdings from being proposed.
 *
 * These are the questions product-service asks of a proposal, asked here
 * instead: it only asks them when the proposal is sent for signature, by which
 * point the advisor has left the step where the weights are. A grouped model is
 * read by its group weights and is whole a group at a time, so the weights that
 * matter for it are not the ones that matter for a flat model.
 */
export const holdingsProblem = (
  model: HouseholdModelPortfolioModel,
): HoldingsProblem | undefined => {
  const securities = getHouseholdModelPortfolioSecurities(model);
  if (securities.length === 0) return 'EMPTY';

  const products = securities.map((security) => security.financialProduct.id);
  if (new Set(products).size !== products.length) return 'DUPLICATE';

  if (!isHouseholdModelPortfolioAssetClassStrategy(model)) {
    return round(sumBy(securities, 'percentage'), 2) === 100
      ? undefined
      : 'WEIGHTS';
  }

  // A grouped proposal is refused outright without them, since they are what
  // says how much of the model each group is answerable for.
  if (isBranchAllocationInvalid(model.assetClassBranches ?? [])) {
    return 'GROUP_WEIGHTS';
  }

  const { groupedSecurities, unassignedSecurities } =
    groupHouseholdModelPortfolioSecuritiesByAssetClass(
      true,
      securities,
      model.assetClassBranches ?? [],
      model.assetClassLevel,
    );

  return validateGroupAllocations(
    groupedSecurities ?? [],
    unassignedSecurities.length,
  )
    ? undefined
    : 'GROUP_HOLDINGS';
};
